import React, { memo } from "react";
import { Box, Typography, Paper, useTheme, useMediaQuery } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BasicInfoIcon from "../../../assets/icons/basic_info.png";
import CourseDiscoveryIcon from "../../../assets/icons/course.png";
import LoanDetailsIcon from "../../../assets/icons/loan.png";
import DoneTickIcon from "../../../assets/icons/done_tick.png";
import ColorCourseIcon from "../../../assets/icons/color_course_icon.png";
import ColorLoanIcon from "../../../assets/icons/color_loan_icon.png";

interface SidebarProps {
  currentStep: number;
  completedSteps: number[];
}

const BRAND = "#0E6B5E";
const ACTIVE_BG = "#F0FDFA";
const MUTED = "#7A8A88";

const Sidebar: React.FC<SidebarProps> = ({ currentStep, completedSteps }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const steps = [
    { id: 1, label: "Basic Info", icon: BasicInfoIcon },
    {
      id: 2,
      label: "Course Discovery",
      icon: currentStep == 1 ? CourseDiscoveryIcon : ColorCourseIcon,
    },
    {
      id: 3,
      label: "Loan Details",
      icon: currentStep < 3 ? LoanDetailsIcon : ColorLoanIcon,
    },
  ];

  console.log("currentStep", currentStep)

  if (isMobile) {
    return (
      <Box
        sx={{
          width: "100%",
          bgcolor: "#fff",
          px: { xs: 2, sm: 3 },
          pb: 2,
          pt: currentStep == 3 ? { xs: 2, sm: 0 } : {}
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            sx={{
              color: BRAND,
              fontWeight: 600,
              fontSize: { xs: "1.05rem", sm: "1.25rem" },
              letterSpacing: 0.2,
            }}
          >
            Your Journey
          </Typography>
          <Typography
            sx={{
              color: "#3D4948",
              fontWeight: 600,
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
            }}
          >
            Step {currentStep} of 3
          </Typography>
        </Box>

        {/* Steps */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", sm: "flex-start" },
            gap: { xs: 1, sm: 4, md: 6 },
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {steps.map((step) => {
            const isCompleted =
              (completedSteps.includes(step.id) && currentStep !== step.id) ||
              (currentStep === 3 && completedSteps?.includes(3));
            const isActive = currentStep === step.id;

            return (
              <Box
                key={step.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: isActive ? 2 : 0,
                  py: isActive ? 2 : 0,
                  borderRadius: "12px",
                  bgcolor: isActive ? ACTIVE_BG : "transparent",
                  transition: "all 0.25s ease",
                  minWidth: "max-content",
                  position: "relative",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={step.icon}
                      alt={`${step.label} icon`}
                      style={{
                        width: isMobile ? 18 : 22,
                        height: isMobile ? 18 : 22,
                        opacity:
                          !isActive && !isCompleted && step.id !== 1 ? 0.85 : 1,
                      }}
                    />
                    
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      fontWeight: isActive || isCompleted ? 700 : 500,
                      color: isActive || isCompleted ? BRAND : MUTED,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>

              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        py: 3,
        height: "100%",
      }}
    >
      {/* Brand */}
      <Box sx={{ px: 1, mb: 1 }}>
        <Typography
          variant="h6"
          sx={{ color: "primary.main", fontWeight: 800, fontSize: "1.1rem" }}
        >
          Your Journey
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#000000", fontSize: "0.8rem" }}
        >
          Step {currentStep} of 3
        </Typography>
      </Box>

      {/* Steps */}
      {steps.map((step) => {
        const isCompleted =
          (completedSteps.includes(step.id) && currentStep !== step.id) ||
          (currentStep === 3 && completedSteps?.includes(3));
        const isActive = currentStep === step.id;

        return (
          <Box
            key={step.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1,
              py: 1.5,
              borderRadius: "8px",
              bgcolor: isActive && !isCompleted ? "#fff" : "transparent",
              border: isActive && !isCompleted ? "3px solid #FFC70333" : "",
              transition: "all 0.25s ease",
            }}
          >
            <img
              src={step.icon}
              alt={`${step.label} icon`}
              style={{
                width: "21px",
                height: "21px",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: isCompleted || isActive ? 600 : 400,
                color: isActive || isCompleted ? BRAND : MUTED,
                lineHeight: 1.2,
              }}
            >
              {step.label}
            </Typography>
            {isCompleted && (
              <img
                src={DoneTickIcon}
                alt={"Done tick icon"}
                style={{
                  width: "21px",
                  height: "21px",
                }}
              />
            )}
          </Box>
        );
      })}

      {/* Trust box */}
      <Box
        sx={{
          mt: "auto",
          mx: 1,
          p: 2,
          bgcolor: "#FFC70333",
          borderRadius: 2.5,
          border: "1px solid #F5E5B8",
          position: "relative",
          bottom: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#D4891A",
            mb: 0.5,
          }}
        >
          Why Choose Us?
        </Typography>
        <Typography
          sx={{ fontSize: "10px", color: "#3D4948", lineHeight: 1.5 }}
        >
          Expert guidance tailored to your academic and financial profile.
        </Typography>
      </Box>
    </Box>
  );
};

export default memo(Sidebar);
